// The UI snapshot (IMPLEMENTATION §2.1 game/snapshot.ts, §2.9 "UI snapshot at 10 Hz"): the few query views
// the chrome shows, built at most `snapshotHz` times a second — never per frame. The expensive R17 v2
// ranking (`query.upgradeOptions`, ≈ 0.25–0.75 ms) is cached per ADVICE KEY (`query.adviceKey`, defined next
// to the model in sim/rules/advice.ts: everything the ranking reads besides cash — the buildings and their
// configuration, Crate stock span, the Gallery gun, the mask, unlocks, goal and the free cells beside
// deposits), so the goal chip re-picks the affordable-or-next option from the cached ranking with the cash.
import type { Int } from '../sim/core/types.ts';
import type { SimDefs } from '../sim/defs.ts';
import {
  adviceKey,
  type EtaFormat,
  type GoalView,
  goalView,
  incomeView,
  pickBest,
  tutorialView,
  type UpgradeOption,
  upgradeOptions,
} from '../sim/query.ts';
import type { SimState } from '../sim/state.ts';

export interface UiSnapshot {
  tick: Int;
  cash: Int;
  lifetime: Int;
  income: {
    /** HUD headline (L12): total $/s over 60 s, always. */
    totalPerSec: Int;
    /** Source of the secondary "auto $X/s" line (young window; the UI smooths it). */
    autoYoungPerSec: Int;
    showAuto: boolean;
    /** Fixed 120 s window (u20 progress / completion), for cards. */
    autoPerSec: Int;
    manualPerSec: Int;
  };
  goal: GoalView | null;
  tip: string | null;
  /** Expansion mask id. */
  board: string;
  unlocked: Int;
  /** Increments whenever the advice key changes (the cached ranking was recomputed). */
  boardVersion: Int;
}

export class SnapshotBuilder {
  private key = '';
  private version = 0;
  private opts: UpgradeOption[] = [];
  private computed = 0;
  private readonly fmt: EtaFormat;

  constructor(fmt: EtaFormat) {
    this.fmt = fmt;
  }

  /** Forget the cache (new sim / defs). */
  reset(): void {
    this.key = '';
  }

  build(s: SimState, d: SimDefs): UiSnapshot {
    const k = adviceKey(s, d);
    if (k !== this.key) {
      this.key = k;
      this.version += 1;
      this.opts = upgradeOptions(s, d);
      this.computed += 1;
    }
    const iv = incomeView(s, d);
    const tv = tutorialView(s, d);
    return {
      tick: s.tick,
      cash: s.wallet.cash,
      lifetime: s.wallet.lifetime,
      income: {
        totalPerSec: iv.totalPerSec,
        autoYoungPerSec: iv.autoYoungPerSec,
        showAuto: iv.showAuto,
        autoPerSec: iv.autoPerSec,
        manualPerSec: iv.manualPerSec,
      },
      goal: goalView(s, d, this.fmt, pickBest(this.opts, s.wallet.cash)),
      tip: tv.tip?.copy ?? null,
      board: d.board.expansions[s.bounds]?.id ?? '',
      unlocked: s.progress.unlocked.length,
      boardVersion: this.version,
    };
  }

  /** How often the ranking was recomputed (cache diagnostics / tests). */
  get rankings(): number {
    return this.computed;
  }
}
